import { z } from "zod";
import { dashboardWarningSchema, dashboardWidgetSchema } from "./dashboard";
import {
  dataStateSchema,
  freshnessSchema,
  qualitySchema,
  reconciliationSchema,
} from "./governance";
import { isoDateTimeSchema, opaqueIdSchema, versionSchema } from "./primitives";

export const top5CategorySchema = z.enum([
  "problems",
  "components",
  "products",
  "suppliers",
  "failure_modes",
]);

const rankedEntitySchema = z
  .object({
    id: opaqueIdSchema,
    code: z.string().trim().min(1).max(200).optional(),
    name: z.string().trim().min(1).max(300),
  })
  .strict();

const top5EvidenceSchema = z
  .object({
    id: opaqueIdSchema,
    type: z.string().trim().min(1).max(100),
    title: z.string().trim().min(1).max(500),
    classification: z.string().trim().min(1).max(100),
  })
  .strict();

const top5ProblemRecordLinkSchema = z
  .object({
    id: opaqueIdSchema,
    displayId: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(500),
    status: z.enum(["DRAFT", "ACTIVE", "ON_HOLD", "ESCALATED", "CLOSED"]),
  })
  .strict();

const top5ProvenanceSchema = z
  .object({
    sourceConnectionId: opaqueIdSchema,
    ingestionRunId: opaqueIdSchema,
    sourceIdentifier: z.string().trim().min(1).max(500),
    sourceName: z.string().trim().min(1).max(300),
    authorityClass: z.string().trim().min(1).max(100),
  })
  .strict();

export const top5RankedIssueSchema = z
  .object({
    id: opaqueIdSchema,
    rank: z.number().int().min(1).max(5),
    category: top5CategorySchema,
    title: z.string().trim().min(1).max(500),
    description: z.string().trim().min(1).max(2_000).optional(),
    rankingBasis: z
      .object({
        label: z.string().trim().min(1).max(300),
        metricKey: z.string().trim().min(1).max(100).optional(),
        value: z.union([z.number().finite(), z.string().trim().min(1), z.null()]),
        unit: z.string().trim().min(1).max(100).optional(),
        reportingWindow: z.string().trim().min(1).max(200).optional(),
      })
      .strict(),
    claimCount: z.number().int().nonnegative().optional(),
    impact: z
      .object({
        amountMinor: z.string().regex(/^-?\d+$/),
        currencyCode: z.string().regex(/^[A-Z]{3}$/),
      })
      .strict()
      .optional(),
    signal: z
      .object({
        id: opaqueIdSchema,
        key: z.string().trim().min(1).max(300),
        type: z.string().trim().min(1).max(100),
        status: z.string().trim().min(1).max(100),
        severity: z.string().trim().min(1).max(100).optional(),
      })
      .strict(),
    entity: rankedEntitySchema.optional(),
    problemRecord: top5ProblemRecordLinkSchema.optional(),
    evidence: z.array(top5EvidenceSchema).max(100).default([]),
    provenance: z.array(top5ProvenanceSchema).min(1).max(100),
    dataState: dataStateSchema,
    authorityClass: z.string().trim().min(1).max(100),
    freshness: freshnessSchema,
    quality: qualitySchema,
    reconciliation: reconciliationSchema,
    sourceSummary: z.string().trim().min(1).max(1_000),
    detectedAt: isoDateTimeSchema,
    dataCutoffAt: isoDateTimeSchema,
  })
  .strict();

export const top5CategoryPayloadSchema = z
  .object({
    category: top5CategorySchema,
    label: z.string().trim().min(1).max(200),
    items: z.array(top5RankedIssueSchema).max(5),
    unavailableReason: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict()
  .superRefine((payload, context) => {
    if (payload.items.some((item) => item.category !== payload.category)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["items"],
        message: "Ranked items must match their category widget.",
      });
    }
  });

export const top5WidgetSchema = dashboardWidgetSchema(top5CategoryPayloadSchema);

export const top5ResponseSchema = z
  .object({
    view: z.literal("top5"),
    schemaVersion: versionSchema,
    tenantId: opaqueIdSchema,
    generatedAt: isoDateTimeSchema,
    dataAsOf: isoDateTimeSchema,
    widgets: z.array(top5WidgetSchema).length(5),
    warnings: z.array(dashboardWarningSchema).max(100).default([]),
  })
  .strict()
  .superRefine((response, context) => {
    const categories = response.widgets.map((widget) => widget.payload.category);
    if (new Set(categories).size !== categories.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["widgets"],
        message: "Top 5 category widgets must be unique.",
      });
    }
    for (const widget of response.widgets) {
      if (widget.dataState === "UNAVAILABLE" && widget.payload.items.length > 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["widgets"],
          message: "Unavailable Top 5 categories cannot contain ranked results.",
        });
      }
      if (widget.dataState !== "UNAVAILABLE" && widget.payload.items.length === 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["widgets"],
          message: "Available Top 5 categories must contain ranked results.",
        });
      }
    }
  });

export type Top5Category = z.infer<typeof top5CategorySchema>;
export type Top5RankedIssue = z.infer<typeof top5RankedIssueSchema>;
export type Top5CategoryPayload = z.infer<typeof top5CategoryPayloadSchema>;
export type Top5Widget = z.infer<typeof top5WidgetSchema>;
export type Top5Response = z.infer<typeof top5ResponseSchema>;

export type Top5Governance = Readonly<{
  dataState: z.infer<typeof dataStateSchema>;
  freshness: z.infer<typeof freshnessSchema>;
  quality: z.infer<typeof qualitySchema>;
  reconciliation: z.infer<typeof reconciliationSchema>;
}>;
